<main class="workspace" [class.dark-theme]="isDarkTheme === 'dark'" [class.review-mode]="workflowMode === 'review'">
  @if (workflowMode === 'import') {
    <section class="import-shell">
      <section class="import-card">
        <div class="import-copy">
          <div class="import-copy-header">
            <h2>Import Loss Run Document</h2>
            <button
              class="theme-toggle import-theme-toggle"
              type="button"
              [class.on]="isDarkTheme === 'dark'"
              (click)="toggleTheme()"
              [attr.aria-label]="isDarkTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
            >
              <span></span>
              <b>{{ isDarkTheme === 'dark' ? 'Light' : 'Dark' }}</b>
            </button>
          </div>
          <p>Upload PDF or Excel files for data extraction</p>
        </div>

        <input 
          #fileInput 
          type="file" 
          accept="application/pdf,.pdf,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" 
          hidden 
          (change)="onFileSelected($event)" 
        />

        <button
          class="file-dropzone"
          type="button"
          [class.has-file]="selectedFile"
          (click)="fileInput.click()"
          (drop)="onFileDrop($event)"
          (dragover)="onDragOver($event)"
        >
          <span class="drop-icon">
            {{ selectedDocumentType === 'pdf' ? 'PDF' : selectedDocumentType === 'excel' ? 'XLSX' : 'DOC' }}
          </span>
          <strong>
            @if (selectedFileCount > 1) {
              {{ selectedFileCount }} Loss Run Documents
            } @else {
              {{ selectedFile ? selectedFile.name : 'Loss Run Document (PDF or Excel)' }}
            }
          </strong>
          <small>
            {{ selectedFileCount ? selectedFileSize + ' · ' + uploadQueueSummary : 'Drop PDF or Excel files here or click to browse' }}
          </small>
          @if (selectedFileCount) {
            <i type="button" (click)="clearSelectedFile($event)">×</i>
          }
        </button>

        @if (uploadQueue.length) {
          <section class="upload-queue-summary" aria-label="Upload queue summary">
            <article>
              <span>Total</span>
              <strong>{{ selectedFileCount }}</strong>
            </article>
            <article>
              <span>Completed</span>
              <strong>{{ completedUploadCount }}</strong>
            </article>
            <article>
              <span>Pending</span>
              <strong>{{ pendingUploadCount }}</strong>
            </article>
            <article [class.has-error]="failedUploadCount">
              <span>Failed</span>
              <strong>{{ failedUploadCount }}</strong>
            </article>
          </section>

          <section class="upload-file-list" aria-label="Selected upload files">
            @for (item of uploadQueue; track item.id) {
              <article 
                [class.done]="item.status === 'done'" 
                [class.error]="item.status === 'error'" 
                [class.running]="item.status === 'reading' || item.status === 'queued' || item.status === 'running'"
              >
                <span class="file-icon">{{ item.documentType === 'pdf' ? '📄' : '📊' }}</span>
                <div class="file-info">
                  <span class="file-name">{{ item.file.name }}</span>
                  <small class="file-status">
                    {{ item.status === 'done' ? 'Extraction complete' : 
                       item.status === 'error' ? item.error || 'Failed' : 
                       item.status === 'pending' ? 'Pending' : item.message }}
                  </small>
                </div>
              </article>
            }
          </section>
        }

        <section 
          class="upload-status-card" 
          [class.error]="uploadStatus === 'error'" 
          [class.done]="uploadStatus === 'done'"
        >
          <div>
            <span>{{ uploadStepLabel }}</span>
            <strong>{{ uploadMessage }}</strong>
            @if (batchId) {
              <small>Batch ID: {{ batchId }}</small>
            }
            @if (uploadError) {
              <small class="upload-error">{{ uploadError }}</small>
            }
          </div>
          <b>{{ isProcessingUpload ? 'Working' : uploadStatus === 'done' ? 'Done' : 'Ready' }}</b>
        </section>

        @if (showProcessingProgress) {
          <section #processingProgressPanel class="processing-progress-panel" aria-label="Processing progress">
            <header>
              <strong>Processing progress</strong>
              <span>{{ processingElapsedLabel }}</span>
            </header>

            <div class="processing-stage-list">
              @for (stage of processingStages; track stage.id) {
                <article [class]="stage.status">
                  <i aria-hidden="true">
                    {{ stage.status === 'done' ? 'OK' : stage.status === 'running' ? '...' : stage.status === 'error' ? '!' : '·' }}
                  </i>
                  <div>
                    <strong>{{ stage.label }}</strong>
                    <small>{{ stage.detail }}</small>
                  </div>
                </article>
              }
            </div>
          </section>
        }

        <button 
          class="process-button" 
          type="button" 
          [disabled]="!canUsePrimaryUploadAction" 
          (click)="handlePrimaryUploadAction()"
        >
          {{ processingButtonLabel }}
        </button>
      </section>
    </section>
  } @else {
    <!-- Review Mode -->
    <section class="shell review-shell">
      @if (connectorPath) {
        <svg class="field-connector-overlay" aria-hidden="true">
          <path class="field-connector-shadow" [attr.d]="connectorPath"></path>
          <path class="field-connector-motion" [attr.d]="connectorPath"></path>
          <circle class="field-connector-endpoint" [attr.cx]="connectorStartX" [attr.cy]="connectorStartY" r="4"></circle>
          <circle class="field-connector-endpoint" [attr.cx]="connectorEndX" [attr.cy]="connectorEndY" r="4"></circle>
        </svg>
      }
      <header class="review-header">
        <div class="brand-line">
          <span class="doc-icon">LR</span>
          <div>
            <h1>Loss Run Review: {{ documentFileName || 'Document' }}</h1>
            <p class="review-subtitle">{{ documentSubtitle }}</p>
          </div>
        </div>

        <div class="header-status">
          @if (feedbackMessage) {
            <span class="feedback-toast" role="status" aria-live="polite">{{ feedbackMessage }}</span>
          }
          <div class="header-review-actions">
            <button class="accept-high" type="button" [disabled]="!canBatchAcceptHigh" (click)="batchAcceptHigh()">Accept High Confidence</button>
            <button class="download-action" type="button" [disabled]="!fields.length" (click)="downloadReviewExcel()">Download Excel</button>
            <button class="download-action" type="button" [disabled]="!canDownloadFalconJson" (click)="downloadFalconJson()">Download Falcon JSON</button>
            <button class="footer-cancel" type="button" (click)="requestCancelReset()">Cancel</button>
            @if (submitted) {
              <button class="footer-submit" type="button" disabled>Submitted</button>
            } @else {
              <button class="footer-submit" type="button" (click)="openSubmitModal()">Submit</button>
            }
          </div>
          <button
            class="theme-toggle review-theme-toggle"
            type="button"
            [class.on]="isDarkTheme === 'dark'"
            (click)="toggleTheme()"
            [attr.aria-label]="isDarkTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
          >
            <span></span>
          </button>
        </div>
      </header>

      <section class="metadata-banner" aria-label="Loss run metadata">
        @for (item of policyMetadataFields; track item.label) {
          @if (item.field) {
            <div
              class="metadata-item"
              role="button"
              tabindex="0"
              [class.low]="item.confidence !== undefined && item.confidence < 80"
              (click)="selectStandaloneField(item.field)"
              (contextmenu)="openMetadataContextMenu($event, item)"
            >
              <span>{{ item.label }}</span>
              <input
                type="text"
                [ngModel]="item.value"
                (click)="$event.stopPropagation()"
                (focus)="selectStandaloneField(item.field)"
                (ngModelChange)="updatePolicyMetadataField(item.label, $event)"
                [attr.aria-label]="item.label"
              />
            </div>
          } @else {
            <div class="metadata-item is-empty" (contextmenu)="openMetadataContextMenu($event, item)">
              <span>{{ item.label }}</span>
              <input
                type="text"
                [ngModel]="item.value"
                (ngModelChange)="updatePolicyMetadataField(item.label, $event)"
                [attr.aria-label]="item.label"
              />
            </div>
          }
        }
      </section>
      @if (metadataContextMenu) {
        <button class="context-menu-backdrop" type="button" aria-label="Close menu" (click)="closeMetadataContextMenu()"></button>
        <div
          class="table-context-menu has-bbox-action"
          [style.left.px]="metadataContextMenu.clientX"
          [style.top.px]="metadataContextMenu.clientY"
          (click)="$event.stopPropagation()"
        >
          <button type="button" (click)="addMetadataBoundingBoxFromMenu()">Add bounding box from PDF</button>
        </div>
      }

      <section class="progress-rail" aria-label="Review progress">
        <span [style.width.%]="completionPercent"></span>
      </section>

      <div class="review-main">
      <section class="review-workbench" [class.table-focus]="claimsTableFocus" [style.--document-pane-width]="documentPaneWidth + '%'">
        <!-- Left: Document Viewer -->
        <article class="document-viewer">
          <div class="viewer-toolbar">
            <button type="button" (click)="prevPage()" [disabled]="currentPage <= 1">&lt;</button>
            <strong>Page {{ currentPage }} / {{ pageCount }}</strong>
            <button type="button" (click)="nextPage()" [disabled]="currentPage >= pageCount">&gt;</button>
            <button type="button" (click)="zoomOut()">-</button>
            <strong>{{ zoomPercent }}%</strong>
            <button type="button" (click)="zoomIn()">+</button>
            <button type="button" (click)="fitToWidth()">Fit</button>
            <button
              class="mark-toggle"
              type="button"
              [class.focus-mode]="documentMarkMode === 'focus'"
              (click)="toggleDocumentMarks()"
            >
              {{ markButtonLabel }}
            </button>
            @if (canSaveSelectedBoundingBox) {
              <button
                class="bbox-save-action"
                type="button"
                [disabled]="bboxSaveInProgress"
                (click)="saveSelectedBoundingBox()"
              >
                {{ bboxSaveInProgress ? 'Saving box...' : 'Save box' }}
              </button>
            }
            @if (pdfOcrStatus !== 'idle' && pdfOcrStatus !== 'error') {
              <span class="pdf-ocr-status" role="status" aria-live="polite">
                {{ pdfOcrStatusLabel }}
              </span>
            }
            @if (bboxExtractionMessage) {
              <span class="pdf-ocr-status" [class.is-error]="bboxExtractionIsError" role="status" aria-live="polite">
                {{ bboxExtractionMessage }}
              </span>
            }
            <div class="pdf-bbox-mode" role="group" aria-label="PDF bounding box layer">
              <span class="pdf-bbox-mode-label" [class.active]="!showTablePdfSourceBoundingBoxes">
                Field boxes ({{ fieldPdfSourceBoundingBoxes.length }})
              </span>
              <label class="pdf-bbox-toggle" [title]="showTablePdfSourceBoundingBoxes ? 'Switch to field boxes' : 'Switch to table boxes'">
                <input
                  type="checkbox"
                  aria-label="Show table boxes instead of field boxes"
                  [checked]="showTablePdfSourceBoundingBoxes"
                  [disabled]="!tablePdfSourceBoundingBoxes.length"
                  (change)="setPdfSourceBoundingBoxLayer($any($event.target).checked)"
                >
                <span class="pdf-bbox-switch" aria-hidden="true"></span>
              </label>
              <span class="pdf-bbox-mode-label" [class.active]="showTablePdfSourceBoundingBoxes">
                Table boxes ({{ tablePdfSourceBoundingBoxes.length }})
              </span>
            </div>
          </div>

          @if (selectedFieldEvidence; as selectedEvidence) {
            <section
              class="evidence-status-panel"
              [class]="'evidence-status-panel state-' + evidenceStateClass(selectedEvidence.field)"
              role="status"
              aria-live="polite"
            >
              <div class="evidence-status-heading">
                <strong>{{ displayFieldLabel(selectedEvidence.field.label) }}</strong>
                <span class="evidence-state-label">{{ evidenceStateLabel(selectedEvidence.field) }}</span>
                @if (selectedEvidence.field.rawValue) {
                  <span>Raw - pending normalization</span>
                }
                <span>Page {{ evidencePageLabel(selectedEvidence.field) }}</span>
                <span>Box {{ evidenceBoxLabel(selectedEvidence.field) }}</span>
              </div>
              <p>{{ sourceEvidence(selectedEvidence.field) }}</p>
              @if (selectedEvidence.evidence.sourceText) {
                <p class="evidence-source-text">Source text: {{ selectedEvidence.evidence.sourceText }}</p>
              }
              @if (selectedEvidence.evidence.reason) {
                <p class="evidence-validation-reason">{{ selectedEvidence.evidence.reason }}</p>
              }
              @for (region of selectedEvidence.evidence.regions; track region.id) {
                <small>{{ evidenceRegionDetails(region) }}</small>
              }
            </section>
          }

          <div
            class="pdf-frame"
            #pdfFrame
            (scroll)="onPdfFrameScroll()"
            [class.annotations-off]="!showAnnotations"
            [class.table-bbox-mode]="showTablePdfSourceBoundingBoxes"
            [class.bbox-editing]="selectedDocumentType === 'pdf' && !!selectedField?.bbox"
          >
            @if (selectedDocumentType === 'pdf') {
              <div class="pdf-pages-stack">
                @for (pageNum of pdfPageNumbers; track pageNum) {
                  <div class="pdf-page-stage" #pageStage [attr.data-page]="pageNum" (click)="onPageClick($event, pageNum)">
                    <canvas #pageCanvas [attr.data-page]="pageNum"></canvas>
                    <div #pageTextLayer class="pdf-text-layer" aria-label="Selectable PDF text"></div>
                    <div class="pdf-ocr-text-layer" aria-label="Selectable OCR source text" (click)="$event.stopPropagation()">
                      @for (region of selectablePdfTextRegionsForPage(pageNum); track region.id) {
                        <span
                          [style.left.%]="region.percentageBox.left"
                          [style.top.%]="region.percentageBox.top"
                          [style.width.%]="region.percentageBox.width"
                          [style.height.%]="region.percentageBox.height"
                          [style.fontSize.px]="selectablePdfTextFontSize(region)"
                        >{{ region.text }}</span>
                      }
                    </div>

                    <div class="search-highlight-layer" aria-hidden="true">
                      @for (m of matchesForPage(pageNum); track m.id) {
                        <span
                          class="search-highlight"
                          [attr.data-match-id]="m.id"
                          [class.active]="m.id === activeMatchId"
                          [style.left.px]="matchLeft(m)"
                          [style.top.px]="matchTop(m)"
                          [style.width.px]="matchWidth(m)"
                          [style.height.px]="matchHeight(m)"
                        ></span>
                      }
                    </div>

                    <div class="document-hotspot-layer" aria-label="Document highlights">
                      @for (field of hotspotsForPage(pageNum); track field.id) {
                        <button
                          class="document-hotspot"
                          type="button"
                          [attr.data-hotspot-id]="field.id"
                          [class.active]="field.id === selectedFieldId"
                          [class.accepted]="field.status === 'accepted'"
                          [class.low]="field.level === 'low'"
                          [class.editable]="canEditBoundingBox(field)"
                          [style.left.px]="hotspotLeft(field)"
                          [style.top.px]="hotspotTop(field)"
                          [style.width.px]="hotspotWidth(field)"
                          [style.height.px]="hotspotHeight(field)"
                          (pointerdown)="startBboxMove($event, field)"
                          (click)="onHotspotClick($event, field)"
                          [attr.aria-label]="'Select ' + displayFieldLabel(field.label)"
                        >
                          <span>{{ displayFieldLabel(field.label) }}</span>
                          @if (canEditBoundingBox(field)) {
                            @for (handle of bboxResizeHandles; track handle.action) {
                              <i
                                [class]="'bbox-resize-handle ' + handle.className"
                                role="presentation"
                                [attr.aria-label]="handle.label"
                                (pointerdown)="startBboxResize($event, field, handle.action)"
                              ></i>
                            }
                          }
                        </button>
                      }
                      @for (item of evidenceRegionsForPage(pageNum); track item.region.id) {
                        <button
                          class="annotation-region"
                          type="button"
                          [class.contextual]="item.field.evidence?.annotationStatus === 'contextual'"
                          [class.accepted]="item.field.status === 'accepted'"
                          [class.low]="item.field.level === 'low'"
                          [class.active]="item.field.id === selectedFieldId"
                          [style.left.%]="item.region.percentageBox?.left"
                          [style.top.%]="item.region.percentageBox?.top"
                          [style.width.%]="item.region.percentageBox?.width"
                          [style.height.%]="item.region.percentageBox?.height"
                          [attr.title]="evidenceRegionDetails(item.region)"
                          [attr.aria-label]="evidenceStateLabel(item.field) + ' for ' + displayFieldLabel(item.field.label)"
                          (click)="selectEvidenceRegion($event, item.field, item.region)"
                        >
                          <span>{{ displayFieldLabel(item.field.label) }}</span>
                        </button>
                      }
                    </div>
                    <div class="pdf-source-bbox-layer" aria-label="Selected API source bounding box">
                      @for (box of pdfSourceBoundingBoxesForPage(pageNum); track box.id) {
                        <div
                          class="pdf-source-bbox"
                          [class.table-level]="box.level === 'table'"
                          [class.field-level]="box.level === 'field'"
                          [style.left.%]="box.percentageBox.left"
                          [style.top.%]="box.percentageBox.top"
                          [style.width.%]="box.percentageBox.width"
                          [style.height.%]="box.percentageBox.height"
                          [attr.aria-label]="box.details"
                        ></div>
                      }
                    </div>
                  </div>
                }
              </div>
            } @else if (selectedDocumentType === 'excel') {
              <div class="excel-viewer">
                @if (excelSheets.length > 1) {
                  <div class="sheet-tabs">
                    @for (sheet of excelSheets; track sheet) {
                      <button type="button" [class.is-active]="sheet === currentSheetName" (click)="switchSheet(sheet)">
                        {{ sheet }}
                      </button>
                    }
                  </div>
                }
                <table class="excel-table">
                  @for (row of excelData; track $index; let ri = $index) {
                    <tr>
                      @for (cell of row; track $index; let ci = $index) {
                        <td
                          [attr.data-excel-cell]="ri + '-' + ci"
                          [class.is-highlighted]="highlightedExcelRow === ri && highlightedExcelCol === ci"
                        >{{ cell }}</td>
                      }
                    </tr>
                  }
                </table>
              </div>
            }
          </div>

          @if (selectedDocumentType === 'pdf') {
            <div class="confidence-legend" aria-label="Confidence legend">
              <span><i class="legend-dot high"></i>High confidence</span>
              <span><i class="legend-dot low"></i>Needs review</span>
              <span><i class="legend-dot table"></i>User edited table/section</span>
            </div>
          }
        </article>

        <div
          class="workbench-divider"
          role="separator"
          aria-label="Resize document and table panels"
          aria-orientation="vertical"
          [attr.aria-valuenow]="documentPaneWidth"
          (pointerdown)="startWorkbenchResize($event)"
        >
          <span aria-hidden="true"></span>
        </div>

        <!-- Right: Review Sidebar -->
        <aside class="review-sidebar">
          <nav class="review-tabs" aria-label="Review sections">
            <button type="button" [class.active]="activeTab === 'summary' && activeExtractionSection === 'claims'" (click)="activeTab = 'summary'; activeExtractionSection = 'claims'">
              <span class="tab-label">Claims</span>
              <span class="tab-count">{{ visibleSourceTableRows.length }}</span>
            </button>
            <button type="button" [class.active]="activeTab === 'summary' && activeExtractionSection === 'policy'" (click)="activeTab = 'summary'; activeExtractionSection = 'policy'">
              <span class="tab-label">Policy</span>
              <span class="tab-count">{{ policyGridColumns.length }}</span>
            </button>
            <button type="button" class="full-table-toggle" [class.active]="claimsTableFocus" (click)="toggleClaimsTableFocus()">
              {{ claimsTableFocus ? 'Show document' : 'Full table' }}
            </button>
          </nav>

          <div class="sidebar-body" (scroll)="onSidebarScroll()">
            @if (activeTab === 'summary') {
              <section class="extraction-sections">
                @if (activeExtractionSection === 'claims' && sourceTableRows.length) {
                  <article class="extraction-card claims-extraction-card">
                    <div class="claims-table-scroll source-table-scroll">
                      <table
                        class="source-row-table"
                        [class.sparse-rows]="visibleSourceTableRows.length > 0 && visibleSourceTableRows.length < 10"
                        [style.--sparse-row-height]="sourceTableDataRowHeight(visibleSourceTableRows.length) + 'px'"
                      >
                        <thead>
                          <tr>
                            @for (column of sourceTableColumns; track column.key) {
                              <th
                                [attr.data-source-column-key]="column.key"
                                [class.column-added-highlight]="highlightedClaimColumnKey === column.key"
                                [style.width.px]="sourceColumnWidth(column, visibleSourceTableRows)"
                                [style.minWidth.px]="sourceColumnWidth(column, visibleSourceTableRows)"
                                [style.maxWidth.px]="sourceColumnWidth(column, visibleSourceTableRows)"
                                (contextmenu)="openClaimHeaderMenu($event, column)"
                              >
                                <button
                                  class="column-sort-button"
                                  type="button"
                                  [attr.title]="displayFieldLabel(column.label)"
                                  (click)="toggleSourceSort(column)"
                                >
                                  <span>{{ compactSourceColumnLabel(column.label) }}</span>
                                  <b
                                    class="sort-indicator"
                                    [class.active]="sourceSortColumnKey === column.key"
                                    [class.desc]="sourceSortColumnKey === column.key && sourceSortDirection === 'desc'"
                                  >
                                    <i class="sort-up"></i>
                                    <i class="sort-down"></i>
                                  </b>
                                </button>
                              </th>
                            }
                          </tr>
                        </thead>
                        <tbody>
                          @for (row of visibleSourceTableRows; track row.id) {
                            <tr
                              [class.low]="row.issueCount"
                              [class.active]="sourceRowHasSelected(row)"
                            >
                              @for (column of sourceTableColumns; track column.key) {
                                <td
                                  [style.width.px]="sourceColumnWidth(column, visibleSourceTableRows)"
                                  [style.minWidth.px]="sourceColumnWidth(column, visibleSourceTableRows)"
                                  [style.maxWidth.px]="sourceColumnWidth(column, visibleSourceTableRows)"
                                  (contextmenu)="openClaimRowMenu($event, row, column)"
                                >
                                  @if (sourceFieldForColumn(row, column); as field) {
                                    <div class="editable-cell" [class.active]="field.id === selectedFieldId">
                                      <input
                                        class="source-table-input"
                                        type="text"
                                        [attr.data-source-field-id]="field.id"
                                        [attr.data-review-target-id]="field.id"
                                        [class.low]="field.level === 'low'"
                                        [class.accepted]="field.status === 'accepted'"
                                        [class.active]="field.id === selectedFieldId"
                                        [class.empty]="!hasValue(tableCellValue(field))"
                                        [class.edited]="isFieldDirty(field)"
                                        [ngModel]="tableCellValue(field)"
                                        (click)="selectSourceTableCell(field, row)"
                                        (focus)="selectSourceTableCell(field, row)"
                                        (ngModelChange)="updateTableCell(field, $event)"
                                        (blur)="finishInlineEdit(field)"
                                      />
                                    </div>
                                  } @else {
                                    <button
                                      class="blank-cell-add"
                                      type="button"
                                      [attr.title]="'Add ' + displayFieldLabel(column.label)"
                                      [attr.aria-label]="'Add ' + displayFieldLabel(column.label)"
                                      (click)="createSourceTableCell(row, column, $event)"
                                    ></button>
                                  }
                                </td>
                              }
                            </tr>
                            @if (selectedFieldInRow(row); as rowField) {
                              <tr class="source-editor-row">
                                <td [attr.colspan]="sourceTableColumns.length">
                                  <section class="selected-item-panel inline-field-editor table-inline-editor" aria-label="Selected cell detail">
                                    <header>
                                      <div>
                                        <h4>Review {{ displayFieldLabel(rowField.label) }}</h4>
                                        <p class="panel-subtitle">{{ fieldPath(rowField) }}</p>
                                      </div>
                                      <!-- <button type="button" class="editor-close" aria-label="Close field review" (click)="closeFieldEditor()">&times;</button> -->
                                    </header>
                                    <div class="claim-editor-content">
                                      <div class="editor-meta">
                                        <span [class.low-conf]="rowField.confidence < 80">{{ rowField.confidence }}% confidence</span>
                                        <span>Page {{ evidencePageLabel(rowField) }}</span>
                                        <span>{{ sourceTypeLabel(rowField) }}</span>
                                      </div>
                                      <label class="value-block"><span>Reviewed value</span><input type="text" class="value-box-input" [(ngModel)]="selectedFieldValue" /></label>
                                      <label class="value-block"><span>Reason</span><select [(ngModel)]="selectedReason"><option value="" disabled>Select reason</option>@for (reason of reasons; track reason) { <option [value]="reason">{{ reason }}</option> }</select></label>
                                      <div class="selected-item-actions">
                                        <button type="button" class="skip-button" (click)="closeFieldEditor()">Cancel</button>
                                        <button type="button" class="approve-button" [disabled]="!canSaveReviewedField" (click)="acceptReviewedField(rowField)">Save</button>
                                      </div>
                                    </div>
                                  </section>
                                </td>
                              </tr>
                            }
                          } @empty {
                            <tr><td class="empty-row">No grouped source rows found.</td></tr>
                          }
                        </tbody>
                      </table>
                      @if (claimHeaderMenu) {
                        <button class="context-menu-backdrop" type="button" aria-label="Close menu" (click)="closeClaimHeaderMenu()"></button>
                        <div
                          class="table-context-menu"
                          [style.left.px]="claimHeaderMenu.clientX"
                          [style.top.px]="claimHeaderMenu.clientY"
                          (click)="$event.stopPropagation()"
                        >
                          <button type="button" (click)="$event.preventDefault(); $event.stopPropagation(); startHeaderAddField($event)">Add field</button>
                          <button type="button" (click)="addRowFromHeader()">Add row</button>
                          <button type="button" class="danger-menu-item" (click)="deleteClaimColumnFromHeader()">Delete column</button>
                        </div>
                      }
                      @if (claimRowMenu) {
                        <button class="context-menu-backdrop" type="button" aria-label="Close menu" (click)="closeClaimRowMenu()"></button>
                        <div
                          class="table-context-menu"
                          [class.has-bbox-action]="canAddBoundingBoxFromMenu(claimRowMenu.field)"
                          [style.left.px]="claimRowMenu.clientX"
                          [style.top.px]="claimRowMenu.clientY"
                          (click)="$event.stopPropagation()"
                        >
                          @if (canAddBoundingBoxFromMenu(claimRowMenu.field)) {
                            <button type="button" (click)="addClaimCellBoundingBoxFromMenu()">Add bounding box from PDF</button>
                          }
                          <button type="button" class="danger-menu-item" (click)="deleteClaimRowFromMenu()">Delete row</button>
                        </div>
                      }
                      @if (tableAddPopover && tableAddPopover.category === 'claims') {
                        <button class="context-menu-backdrop" type="button" aria-label="Close add field" (click)="cancelTableAdd()"></button>
                        <div
                          class="table-add-popover"
                          [style.left.px]="tableAddPopover.clientX"
                          [style.top.px]="tableAddPopover.clientY"
                          (click)="$event.stopPropagation()"
                        >
                          <input type="search" [(ngModel)]="tableAddPopover.search" placeholder="Search field..." />
                          <div class="field-option-list" role="listbox" aria-label="Fields">
                            @for (option of filteredClaimAddFieldOptions; track option.key) {
                              <button
                                type="button"
                                role="option"
                                [class.selected]="tableAddPopover.optionKey === option.key"
                                (click)="tableAddPopover.optionKey = option.key"
                              >
                                <span>{{ option.display }}</span>
                              </button>
                            }
                          </div>
                          <div class="table-add-popover-actions">
                            <button type="button" class="skip-button" (click)="cancelTableAdd()">Cancel</button>
                            <button type="button" class="approve-button" [disabled]="!tableAddPopover.optionKey" (click)="confirmHeaderAddField()">Add field</button>
                          </div>
                        </div>
                      }
                    </div>
                  </article>
                }

                @if (activeExtractionSection === 'policy' && policyGridColumns.length) {
                  <article class="extraction-card policy-extraction-card">
                    <div class="claims-table-scroll source-table-scroll policy-unified-scroll">
                      <table
                        class="source-row-table policy-unified-table"
                        [class.sparse-rows]="policyGridRows.length > 0 && policyGridRows.length < 10"
                        [style.--sparse-row-height]="sourceTableDataRowHeight(policyGridRows.length) + 'px'"
                      >
                        <thead>
                          <tr>
                            @for (column of policyGridColumns; track column.key) {
                              <th
                                [attr.data-source-column-key]="column.key"
                                [class.column-added-highlight]="highlightedClaimColumnKey === column.key"
                                [style.width.px]="sourceColumnWidth(column, policyGridRows)"
                                [style.minWidth.px]="sourceColumnWidth(column, policyGridRows)"
                                [style.maxWidth.px]="sourceColumnWidth(column, policyGridRows)"
                                (contextmenu)="openPolicyHeaderMenu($event, column)"
                              >
                                <button class="column-sort-button" type="button" [attr.title]="displayFieldLabel(column.label)">
                                  <span>{{ compactSourceColumnLabel(column.label) }}</span>
                                  <b class="sort-indicator">
                                    <i class="sort-up"></i>
                                    <i class="sort-down"></i>
                                  </b>
                                </button>
                              </th>
                            }
                          </tr>
                        </thead>
                        <tbody>
                          @for (row of policyGridRows; track row.id) {
                            <tr
                              [class.low]="row.issueCount"
                              [class.active]="sourceRowHasSelected(row)"
                            >
                              @for (column of policyGridColumns; track column.key) {
                                <td
                                  [style.width.px]="sourceColumnWidth(column, policyGridRows)"
                                  [style.minWidth.px]="sourceColumnWidth(column, policyGridRows)"
                                  [style.maxWidth.px]="sourceColumnWidth(column, policyGridRows)"
                                  (contextmenu)="openPolicyRowMenu($event, row, column)"
                                >
                                  @if (sourceFieldForColumn(row, column); as field) {
                                    <div
                                      class="editable-cell"
                                      [class.active]="field.id === selectedFieldId"
                                      (click)="selectSourceTableCell(field, row)"
                                    >
                                      <input
                                        class="source-table-input"
                                        type="text"
                                        [attr.data-source-field-id]="field.id"
                                        [attr.data-review-target-id]="field.id"
                                        [class.low]="field.level === 'low'"
                                        [class.accepted]="field.status === 'accepted'"
                                        [class.empty]="!hasValue(tableCellValue(field))"
                                        [class.edited]="isFieldDirty(field)"
                                        [ngModel]="tableCellValue(field)"
                                        (click)="selectSourceTableCell(field, row)"
                                        (focus)="selectSourceTableCell(field, row)"
                                        (ngModelChange)="updateTableCell(field, $event)"
                                        (blur)="finishInlineEdit(field)"
                                      />
                                    </div>
                                  } @else {
                                    <button
                                      class="blank-cell-add"
                                      type="button"
                                      [attr.title]="'Add ' + displayFieldLabel(column.label)"
                                      [attr.aria-label]="'Add ' + displayFieldLabel(column.label)"
                                      (click)="createPolicyTableCell(row, column, $event)"
                                    ></button>
                                  }
                                </td>
                              }
                            </tr>
                            @if (selectedFieldInRow(row); as rowField) {
                              <tr class="source-editor-row">
                                <td [attr.colspan]="policyGridColumns.length">
                                  <section class="selected-item-panel inline-field-editor table-inline-editor" aria-label="Selected policy cell detail">
                                    <header>
                                      <div>
                                        <h4>Review {{ displayFieldLabel(rowField.label) }}</h4>
                                        <p class="panel-subtitle">{{ fieldPath(rowField) }}</p>
                                      </div>
                                      <!-- <button type="button" class="editor-close" aria-label="Close field review" (click)="closeFieldEditor()">&times;</button> -->
                                    </header>
                                    <div class="claim-editor-content">
                                      <div class="editor-meta">
                                        <span [class.low-conf]="rowField.confidence < 80">{{ rowField.confidence }}% confidence</span>
                                        <span>Page {{ evidencePageLabel(rowField) }}</span>
                                        <span>{{ sourceTypeLabel(rowField) }}</span>
                                      </div>
                                      <label class="value-block"><span>Reviewed value</span><input type="text" class="value-box-input" [(ngModel)]="selectedFieldValue" /></label>
                                      <label class="value-block"><span>Reason</span><select [(ngModel)]="selectedReason"><option value="" disabled>Select reason</option>@for (reason of reasons; track reason) { <option [value]="reason">{{ reason }}</option> }</select></label>
                                      <div class="selected-item-actions">
                                        <button type="button" class="skip-button" (click)="closeFieldEditor()">Cancel</button>
                                        <button type="button" class="approve-button" [disabled]="!canSaveReviewedField" (click)="acceptReviewedField(rowField)">Save</button>
                                      </div>
                                    </div>
                                  </section>
                                </td>
                              </tr>
                            }
                          } @empty {
                            <tr><td class="empty-row">No policy rows found.</td></tr>
                          }
                        </tbody>
                      </table>
                      @if (policyHeaderMenu) {
                        <button class="context-menu-backdrop" type="button" aria-label="Close menu" (click)="closePolicyHeaderMenu()"></button>
                        <div
                          class="table-context-menu"
                          [style.left.px]="policyHeaderMenu.clientX"
                          [style.top.px]="policyHeaderMenu.clientY"
                          (click)="$event.stopPropagation()"
                        >
                          <button type="button" (click)="$event.preventDefault(); $event.stopPropagation(); startPolicyHeaderAddField($event)">Add field</button>
                          <button type="button" (click)="addPolicyRowFromHeader()">Add row</button>
                          <button type="button" class="danger-menu-item" (click)="deletePolicyColumnFromHeader()">Delete column</button>
                        </div>
                      }
                      @if (policyRowMenu) {
                        <button class="context-menu-backdrop" type="button" aria-label="Close menu" (click)="closePolicyRowMenu()"></button>
                        <div
                          class="table-context-menu"
                          [class.has-bbox-action]="canAddBoundingBoxFromMenu(policyRowMenu.field)"
                          [style.left.px]="policyRowMenu.clientX"
                          [style.top.px]="policyRowMenu.clientY"
                          (click)="$event.stopPropagation()"
                        >
                          @if (canAddBoundingBoxFromMenu(policyRowMenu.field)) {
                            <button type="button" (click)="addPolicyCellBoundingBoxFromMenu()">Add bounding box from PDF</button>
                          }
                          <button type="button" class="danger-menu-item" (click)="deletePolicyRowFromMenu()">Delete row</button>
                        </div>
                      }
                      @if (tableAddPopover && tableAddPopover.category === 'policy') {
                        <button class="context-menu-backdrop" type="button" aria-label="Close add field" (click)="cancelTableAdd()"></button>
                        <div
                          class="table-add-popover"
                          [style.left.px]="tableAddPopover.clientX"
                          [style.top.px]="tableAddPopover.clientY"
                          (click)="$event.stopPropagation()"
                        >
                          <input type="search" [(ngModel)]="tableAddPopover.search" placeholder="Search field..." />
                          <div class="field-option-list" role="listbox" aria-label="Fields">
                            @for (option of filteredPolicyAddFieldOptions; track option.key) {
                              <button
                                type="button"
                                role="option"
                                [class.selected]="tableAddPopover.optionKey === option.key"
                                (click)="tableAddPopover.optionKey = option.key"
                              >
                                <span>{{ option.display }}</span>
                              </button>
                            }
                          </div>
                          <div class="table-add-popover-actions">
                            <button type="button" class="skip-button" (click)="cancelTableAdd()">Cancel</button>
                            <button type="button" class="approve-button" [disabled]="!tableAddPopover.optionKey" (click)="confirmHeaderAddField()">Add field</button>
                          </div>
                        </div>
                      }
                    </div>
                  </article>
                }

                @if (false && activeExtractionSection === 'financial' && (financialTableRows.length || financialSummaryFields.length)) {
                  <article class="extraction-card financial-extraction-card">
                    <header class="extraction-card-header">
                      <div>
                        <h3>Financial Summary</h3>
                        <span>
                          @if (financialTableRows.length) {
                            {{ financialTableRows.length }} rows extracted
                          } @else {
                            {{ financialSummaryFields.length }} fields
                          }
                        </span>
                      </div>
                      @if (financialTableRows.length) {
                        <div class="extraction-actions">
                          <span>{{ financialTableIssueCount }} need review</span>
                        </div>
                      }
                    </header>

                    @if (financialTableRows.length) {
                      <div class="claims-table-scroll source-table-scroll">
                        <table class="source-row-table">
                          <thead>
                          <tr>
                            @for (column of financialTableColumns; track column.key) {
                              <th>{{ displayFieldLabel(column.label) }}</th>
                            }
                          </tr>
                          </thead>
                          <tbody>
                            @for (row of financialTableRows; track row.id) {
                              <tr [class.low]="row.issueCount" [class.active]="sourceRowHasSelected(row)">
                                @for (column of financialTableColumns; track column.key) {
                                  <td>
                                    @if (sourceFieldForColumn(row, column); as field) {
                                      <div class="editable-cell" [class.active]="field.id === selectedFieldId">
                                        <input
                                          class="source-table-input"
                                          type="text"
                                          [attr.data-source-field-id]="field.id"
                                          [attr.data-review-target-id]="field.id"
                                          [class.low]="field.level === 'low'"
                                          [class.accepted]="field.status === 'accepted'"
                                          [class.active]="field.id === selectedFieldId"
                                          [class.empty]="!hasValue(tableCellValue(field))"
                                          [class.edited]="isFieldDirty(field)"
                                          [ngModel]="tableCellValue(field)"
                                          (click)="selectSourceTableCell(field, row)"
                                          (focus)="selectSourceTableCell(field, row)"
                                          (ngModelChange)="updateTableCell(field, $event)"
                                          (blur)="finishInlineEdit(field)"
                                        />
                                      </div>
                                    }
                                  </td>
                                }
                              </tr>
                              @if (selectedFieldInRow(row); as rowField) {
                                <tr class="source-editor-row">
                                  <td [attr.colspan]="financialTableColumns.length">
                                    <section class="selected-item-panel inline-field-editor table-inline-editor" aria-label="Selected financial cell detail">
                                      <header>
                                        <div>
                                          <h4>Review {{ displayFieldLabel(rowField.label) }}</h4>
                                          <p class="panel-subtitle">{{ fieldPath(rowField) }}</p>
                                        </div>
                                        <button type="button" class="editor-close" aria-label="Close field review" (click)="closeFieldEditor()">&times;</button>
                                      </header>
                                      <div class="claim-editor-content">
                                        <div class="editor-meta">
                                          <span [class.low-conf]="rowField.confidence < 80">{{ rowField.confidence }}% confidence</span>
                                          <span>Page {{ evidencePageLabel(rowField) }}</span>
                                          <span>{{ sourceTypeLabel(rowField) }}</span>
                                        </div>
                                        <label class="value-block"><span>Reviewed value</span><input type="text" class="value-box-input" [(ngModel)]="selectedFieldValue" /></label>
                                        <label class="value-block"><span>Reason</span><select [(ngModel)]="selectedReason"><option value="" disabled>Select reason</option>@for (reason of reasons; track reason) { <option [value]="reason">{{ reason }}</option> }</select></label>
                                        <div class="selected-item-actions">
                                          <button type="button" class="skip-button" (click)="closeFieldEditor()">Cancel</button>
                                          <button type="button" class="approve-button" [disabled]="!canSaveReviewedField" (click)="acceptReviewedField(rowField)">Save</button>
                                        </div>
                                      </div>
                                    </section>
                                  </td>
                                </tr>
                              }
                            }
                          </tbody>
                        </table>
                      </div>
                    }

                    @if (financialSummaryFields.length) {
                      <table class="mini-field-table" [class.has-top-border]="financialTableRows.length">
                        <tbody>
                          @for (field of financialSummaryFields; track field.id) {
                            <tr
                              [attr.data-field-id]="field.id"
                              [class.active]="field.id === selectedFieldId"
                              (click)="selectStandaloneField(field)"
                            >
                              <th>{{ displayFieldLabel(field.label) }}</th>
                              <td>
                                <div class="editable-cell mini-editable-cell" [class.active]="field.id === selectedFieldId">
                                  <input
                                    class="mini-field-input"
                                    type="text"
                                    [attr.data-review-target-id]="field.id"
                                    [class.low]="field.level === 'low'"
                                    [class.accepted]="field.status === 'accepted'"
                                    [class.empty]="!hasValue(tableCellValue(field))"
                                    [class.edited]="isFieldDirty(field)"
                                    [ngModel]="tableCellValue(field)"
                                    (click)="selectStandaloneField(field)"
                                    (focus)="selectStandaloneField(field)"
                                    (ngModelChange)="updateTableCell(field, $event)"
                                    (blur)="finishInlineEdit(field)"
                                  />
                                </div>
                              </td>
                              <td class="mini-confidence" [class.low]="field.confidence < 80">{{ field.confidence }}%</td>
                            </tr>
                            @if (selectedStandaloneField(field)) {
                              <tr class="source-editor-row mini-editor-row">
                                <td colspan="3">
                                  <section class="selected-item-panel inline-field-editor table-inline-editor" aria-label="Selected financial field detail">
                                    <header>
                                      <div>
                                        <h4>Review {{ displayFieldLabel(field.label) }}</h4>
                                        <p class="panel-subtitle">{{ fieldPath(field) }}</p>
                                      </div>
                                      <button type="button" class="editor-close" aria-label="Close field review" (click)="closeFieldEditor()">&times;</button>
                                    </header>
                                    <div class="claim-editor-content">
                                      <div class="editor-meta">
                                        <span [class.low-conf]="field.confidence < 80">{{ field.confidence }}% confidence</span>
                                        <span>Page {{ evidencePageLabel(field) }}</span>
                                        <span>{{ sourceTypeLabel(field) }}</span>
                                      </div>
                                      <label class="value-block"><span>Reviewed value</span><input type="text" class="value-box-input" [(ngModel)]="selectedFieldValue" /></label>
                                      <label class="value-block"><span>Reason</span><select [(ngModel)]="selectedReason"><option value="" disabled>Select reason</option>@for (reason of reasons; track reason) { <option [value]="reason">{{ reason }}</option> }</select></label>
                                      <div class="selected-item-actions">
                                        <button type="button" class="skip-button" (click)="closeFieldEditor()">Cancel</button>
                                        <button type="button" class="approve-button" [disabled]="!canSaveReviewedField" (click)="acceptReviewedField(field)">Save</button>
                                      </div>
                                    </div>
                                  </section>
                                </td>
                              </tr>
                            }
                          }
                        </tbody>
                      </table>
                    }
                  </article>
                }

              </section>

            }

            @if (activeTab === 'validation') {
              <section class="validation-section">
                @for (field of validationFields; track field.id) {
                  <article class="validation-row" [class.error]="!hasValue(field.value)" [class.ok]="hasValue(field.value)">
                    <span class="validation-field">{{ displayFieldLabel(field.label) }}</span>
                    <span class="validation-value">{{ displayValue(field.value) }}</span>
                    <span class="validation-badge">
                      {{ hasValue(field.value) ? 'OK' : 'Missing' }}
                    </span>
                  </article>
                } @empty {
                  <div class="empty-tab"><p>No required fields.</p></div>
                }
              </section>
            }

            @if (activeTab === 'change-log') {
              <section class="change-log-section">
                @for (entry of changeLog; track entry.id) {
                  <article class="change-log-row">
                    <div class="change-log-head">
                      <strong>{{ entry.field }}</strong>
                      <span class="change-action">{{ entry.action }}</span>
                      <small>{{ entry.timestamp }}</small>
                    </div>
                    <div class="change-log-values">
                      <span class="old-value">{{ entry.oldValue || '—' }}</span>
                      <span class="arrow">→</span>
                      <span class="new-value">{{ entry.newValue || '—' }}</span>
                    </div>
                  </article>
                } @empty {
                  <div class="empty-tab"><p>Reviewer changes will appear here.</p></div>
                }
              </section>
            }
          </div>
        </aside>
      </section>

      @if (false && activeTab === 'summary' && selectedField) {
        <div class="below-pdf-row">
        <section class="selected-item-panel below-pdf" aria-label="Selected field detail">
          <header>
            <h4>Review selected field</h4>
            <p class="panel-subtitle">Source and extraction shown together</p>
          </header>

          <div class="panel-body panel-body-columns">
            <div class="panel-col">
              <span class="needs-confirmation" [class.ok]="hasValue(selectedField.value) && selectedField.level === 'high'">
                {{ hasValue(selectedField.value) && selectedField.level === 'high' ? 'Looks good' : 'Needs human confirmation' }}
              </span>
              <h5 class="field-name">{{ selectedField.label }}</h5>
              <span class="field-path">{{ fieldPath(selectedField) }}</span>

              <div class="value-block">
                <span>Extracted value</span>
                <input type="text" class="value-box-input" [(ngModel)]="selectedFieldValue" />
              </div>

              <div class="value-block">
                <span>Source evidence</span>
                <div class="value-box muted">{{ sourceEvidence(selectedField) }}</div>
              </div>
            </div>

            <div class="panel-col">
              <div class="meta-row">
                <div class="meta-box">
                  <span>Confidence</span>
                  <strong [class.low-conf]="selectedField.level === 'low'">{{ selectedField.confidence }}%</strong>
                </div>
                <div class="meta-box">
                  <span>Page</span>
                  <strong>{{ evidencePageLabel(selectedField) }}</strong>
                </div>
              </div>

              <div class="value-block">
                <span>Normalized</span>
                <div class="value-box muted">{{ normalizedNote(selectedField) }}</div>
              </div>

              <div class="checks-block">
                <span>Automated checks</span>
                @for (check of selectedFieldChecks; track check.label) {
                  <div class="check-row" [class.warn]="!check.ok">
                    <i aria-hidden="true">{{ check.ok ? 'OK' : '!' }}</i>{{ check.label }}
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="selected-item-actions">
            <button type="button" class="flag-button" (click)="flagIssue(selectedField)">Flag issue</button>
            <button type="button" class="skip-button" (click)="skipField(selectedField)">Skip</button>
          </div>
          <button type="button" class="approve-button" (click)="acceptField(selectedField)">Approve field</button>
        </section>
        </div>
      }

      @if (false && selectedDocumentType === 'pdf' && sourceTableRows.length) {
      <section class="source-table-full" aria-label="Extracted source row review">
        <header class="claims-table-header">
          <div>
            <h3>Source Row Review</h3>
          </div>
          <div class="claims-table-stats">
            <span class="stat-pill">{{ sourceTableRows.length }} rows</span>
            <span class="stat-pill need-review">{{ sourceTableIssueCount }} rows need review</span>
            <button type="button" class="stat-pill issues-toggle" [class.active]="sourceRowsIssuesOnly" (click)="toggleSourceRowsIssuesOnly()">
              Show issues only
            </button>
          </div>
        </header>

        <div class="claims-table-scroll source-table-scroll">
          <table class="source-row-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Page</th>
                @for (column of sourceTableColumns; track column.key) {
                  <th>{{ displayFieldLabel(column.label) }}</th>
                }
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              @for (row of visibleSourceTableRows; track row.id; let rowIndex = $index) {
                <tr [class.low]="row.issueCount" [class.active]="sourceRowHasSelected(row)">
                  <td>{{ rowIndex + 1 }}</td>
                  <td>
                    @if (sourceRowNavigationPage(row); as sourcePage) {
                      <button type="button" class="page-jump" (click)="scrollToPage(sourcePage)">
                        Page {{ sourcePage }}
                      </button>
                    } @else {
                      <span class="page-unavailable">Page &mdash;</span>
                    }
                    <small>{{ row.confidence }}% min confidence</small>
                  </td>
                  @for (column of sourceTableColumns; track column.key) {
                    <td>
                      @if (sourceFieldForColumn(row, column); as field) {
                        <input
                          class="source-table-input"
                          type="text"
                          [attr.data-source-field-id]="field.id"
                          [class.low]="field.level === 'low'"
                          [class.accepted]="field.status === 'accepted'"
                          [class.active]="field.id === selectedFieldId"
                          [class.empty]="!hasValue(tableCellValue(field))"
                          [class.edited]="isFieldDirty(field)"
                          [ngModel]="tableCellValue(field)"
                          (ngModelChange)="updateTableCell(field, $event)"
                          (focus)="selectSourceTableCell(field, row)"
                        />
                      }
                    </td>
                  }
                  <td>
                    <button type="button" class="row-accept-button" [disabled]="!row.issueCount" (click)="acceptSourceRow(row)">
                      Accept row
                    </button>
                  </td>
                </tr>
              } @empty {
                <tr><td colspan="3" class="empty-row">No grouped source rows found.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </section>
      }

      @if (selectedDocumentType !== 'pdf') {
      <section class="claims-table-full" aria-label="Claims table review">
        <header class="claims-table-header">
          <div>
            <h3>Claims Table Review</h3>
            <p>Wide table review lives below the PDF so users are not forced into a cramped right panel.</p>
          </div>
          <div class="claims-table-stats">
            <span class="stat-pill need-review">{{ claimsNeedReviewCount }} claims need review</span>
            <span class="stat-pill high-conf">{{ claimsAcceptedCount }} accepted</span>
            <button type="button" class="stat-pill issues-toggle" [class.active]="claimsIssuesOnly" (click)="toggleClaimsIssuesOnly()">
              Show issues only
            </button>
          </div>
        </header>

        <div class="claims-table-scroll">
          <table class="claims-table">
            <thead>
              <tr>
                <th>Claim #</th>
                <th>Claimant</th>
                <th>Loss Date</th>
                <th>Status</th>
                <th>Coverage</th>
                <th>Paid</th>
                <th>Reserves</th>
                <th>Confidence</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (claim of visibleClaims; track claim.id) {
                <tr
                  [class.low]="claim.level === 'low'"
                  [class.accepted]="claim.reviewStatus === 'accepted'"
                >
                  <td>{{ claim.claimNumber }}</td>
                  <td>{{ claim.claimant }}</td>
                  <td>{{ claim.dateOfLoss }}</td>
                  <td><span class="status-pill" [class.closed]="claim.status === 'Closed'">{{ claim.status }}</span></td>
                  <td>{{ claim.coverage }}</td>
                  <td>{{ claim.paid }}</td>
                  <td>{{ claim.reserves }}</td>
                  <td><span [class.low-conf]="claim.level === 'low'">{{ claim.confidence }}%</span></td>
                  <td>
                    @if (claim.reviewStatus === 'accepted') {
                      <span class="accepted-tick" title="Accepted" aria-hidden="true">OK</span>
                    } @else {
                      <button class="icon-action accept-mini" type="button" title="Accept" aria-label="Accept claim" (click)="acceptClaim(claim)">
                        <span aria-hidden="true">OK</span>
                      </button>
                    }
                  </td>
                </tr>
              } @empty {
                <tr><td colspan="9" class="empty-row">No claims extracted.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </section>
      }
      </div>

    </section>

    @if (false && addFieldPopover) {
      <div
        class="add-field-popover"
        [style.left.px]="addFieldPopover.clientX"
        [style.top.px]="addFieldPopover.clientY"
      >
        <label class="add-field-value">
          <span>Value to add</span>
          <input type="text" [(ngModel)]="addFieldPopover.text" placeholder="Type value here" />
        </label>
        <select [(ngModel)]="addFieldPopover.optionKey">
          <option value="">Select field...</option>
          @for (option of availableFieldLabels; track option.key) {
            <option [value]="option.key">{{ option.display }}</option>
          }
        </select>
        <div class="add-field-actions">
          <button type="button" class="cancel-btn" (click)="cancelAddField()">Cancel</button>
          <button type="button" class="add-btn" [disabled]="!addFieldPopover.optionKey || !addFieldPopover.text.trim()" (click)="confirmAddField()">
            + Add to extracted fields
          </button>
        </div>
      </div>
    }

    @if (showCancelModal) {
      <section class="submit-modal-backdrop" role="dialog" aria-modal="true">
        <article class="submit-modal cancel-modal">
          <h2>Cancel Review?</h2>
          <p class="submit-copy">Are you sure? The current document and review changes will be cleared.</p>
          <div class="submit-warning">
            <strong>!</strong>
            <span>This will reset back to the upload screen.</span>
          </div>
          <footer class="submit-modal-actions">
            <button class="footer-cancel" type="button" (click)="closeCancelModal()">Keep Reviewing</button>
            <button class="footer-submit danger-submit" type="button" (click)="confirmCancelReset()">Yes, Reset</button>
          </footer>
        </article>
      </section>
    }

    @if (showSubmitModal) {
      <section class="submit-modal-backdrop" role="dialog" aria-modal="true">
        <article class="submit-modal">
          <h2>Confirm Submission</h2>
          <p class="submit-copy">Review the summary below. Once submitted, the data will be finalized.</p>
          <dl class="submit-summary">
            <div><dt>Fields extracted</dt><dd>{{ totalExtractedFieldCount }}</dd></div>
            <div class="accepted-row"><dt>Accepted</dt><dd>{{ acceptedCount }}</dd></div>
            <div class="edited-row"><dt>Edited</dt><dd>{{ editedCount }}</dd></div>
            <div class="rejected-row"><dt>Rejected</dt><dd>{{ rejectedCount }}</dd></div>
            <div class="pending-row"><dt>Pending</dt><dd>{{ pendingCount }}</dd></div>
          </dl>
          @if (submitError) {
            <p class="submit-error" role="alert">{{ submitError }}</p>
          }
          <footer class="submit-modal-actions">
            <button class="footer-cancel" type="button" (click)="closeSubmitModal()">Back</button>
            <button class="footer-submit" type="button" [disabled]="isSubmitting" (click)="confirmSubmit()">
              {{ isSubmitting ? 'Submitting...' : 'Submit' }}
            </button>
          </footer>
        </article>
      </section>
    }
  }
</main>
